import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { getBlog } from "../lib/api";
import Footer from "../components/site/Footer";

const Block = ({ block, index }) => {
  if (block.type === "quote") {
    return (
      <blockquote className="my-12 border-l-2 border-[#FAFAFA] pl-6 md:pl-8">
        <p className="font-serif italic text-2xl md:text-3xl leading-snug text-[#FAFAFA]">{block.text}</p>
      </blockquote>
    );
  }
  if (block.type === "verse") {
    return <p className="verse">{block.text}</p>;
  }
  if (block.type === "video") {
    return (
      <figure className="my-12 -mx-2 md:-mx-16">
        <div className="relative w-full overflow-hidden border border-[#1c1c1c] bg-black aspect-video">
          <iframe
            data-testid="blog-video"
            title="Video"
            src={`https://www.youtube.com/embed/${block.youtube_id}`}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </figure>
    );
  }
  if (block.type === "img") {
    return (
      <figure className="my-12">
        <img src={block.url} alt="" className="w-full grayscale border border-[#1c1c1c]" />
      </figure>
    );
  }
  // paragraph, with drop cap on first
  if (index === 0) {
    const first = block.text.charAt(0);
    const rest = block.text.slice(1);
    return (
      <p>
        <span className="float-left font-serif text-7xl md:text-8xl leading-[0.7] pr-3 pt-2 text-[#FAFAFA]">
          {first}
        </span>
        {rest}
      </p>
    );
  }
  return <p>{block.text}</p>;
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    window.scrollTo(0, 0);
    getBlog(slug)
      .then(setBlog)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6">
        <h1 className="font-serif text-4xl text-[#FAFAFA]">Not found</h1>
        <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#FAFAFA]">
          ← Back home
        </Link>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#5a5a5a] animate-pulse">Loading…</div>
      </div>
    );
  }

  return (
    <article data-testid="blog-post-page">
      {/* reading progress */}
      <motion.div
        style={{ scaleX }}
        className="fixed top-0 left-0 right-0 h-[3px] bg-[#FAFAFA] origin-left z-[60]"
        data-testid="reading-progress"
      />

      {/* hero */}
      <header className="relative min-h-[80svh] w-full overflow-hidden flex items-end">
        <img
          src={blog.cover}
          alt={blog.title}
          className="absolute inset-0 h-full w-full object-cover grayscale opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="relative z-10 mx-auto max-w-[900px] w-full px-6 md:px-10 pb-16">
          <button
            data-testid="blog-back-btn"
            onClick={() => navigate("/#writing")}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#FAFAFA] mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> All writing
          </button>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#c9c9c9] mb-4">
            {blog.category} · {blog.read_time}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tighter text-[#FAFAFA] leading-[0.92]"
          >
            {blog.title}
          </motion.h1>
          <p className="text-[#a8a8a8] mt-5 text-lg max-w-xl">{blog.subtitle}</p>
        </div>
      </header>

      {/* body */}
      <div className="mx-auto max-w-[720px] px-6 md:px-8 py-16 md:py-24 blog-body">
        {blog.body.map((block, i) => (
          <Block key={i} block={block} index={i} />
        ))}

        <div className="mt-20 pt-10 border-t border-[#1c1c1c] flex items-center justify-between">
          <span className="font-serif italic text-xl text-[#8f8f8f]">— Soham</span>
          <Link
            to="/#writing"
            className="font-mono text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#FAFAFA] transition-colors"
          >
            More essays →
          </Link>
        </div>
      </div>

      <Footer />
    </article>
  );
}
